'use client';

import Logo from '@/assets/icons/Logo';
import ManageConsentPreferences from '@/components/globals/CookiePreferences/ManageConsentPreferences';
import NavigationLink from '@/components/NavigationLink';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useEffect, useRef, useState } from 'react';

const cookiePreferencesName = 'ouinex-cookie-preferences';

export default function CookiePreferences({ data = {} }) {
  const {
    title,
    body,
    cookiePolicyLink,
    cookiePolicyLabel,
    allowAllLabel,
    rejectLabel,
    confirmLabel,
    cookiePreferenceLabel,
    ...rest
  } = data;

  const [open, setOpen] = useState(false);
  const { preferences = [] } = rest;
  const closeTimeoutRef = useRef(null);

  const preferencesWithSwitch = preferences.filter(preference => preference?.haveSwitch);

  const setInitialState = (checked = true) =>
    preferencesWithSwitch.reduce(
      (prev, preference) => ({
        ...prev,
        [preference.id]: {
          checked
        }
      }),
      {}
    );

  const [checkListMap, setCheckListMap] = useState(() => {
    const cookiePreferences = typeof window === 'undefined' ? undefined : localStorage.getItem(cookiePreferencesName);
    if (cookiePreferences) return JSON.parse(cookiePreferences);
    return setInitialState();
  });

  const isSomeUnchecked = preferencesWithSwitch.some(preference => !checkListMap[preference.id]?.checked);

  const _clearTimeout = () => {
    if (closeTimeoutRef.current) {
      setOpen(true);
      clearTimeout(closeTimeoutRef.current);
    }
  };

  const handleAllowAll = () => {
    setCheckListMap(setInitialState());
    _clearTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setOpen(false);
    }, 200);
  };

  const handleConfirm = () => {
    setOpen(false);
  };

  const handleReject = () => {
    setCheckListMap(setInitialState(false));
    _clearTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setOpen(false);
    }, 200);
  };

  useEffect(() => {
    return () => _clearTimeout();
  }, []);

  return (
    <Dialog open={open}>
      {cookiePreferenceLabel && <DialogTrigger onClick={() => setOpen(true)} className="hover:text-primary">{cookiePreferenceLabel}</DialogTrigger>}
      <DialogContent className='bg-white text-background p-0 gap-0 max-h-[calc(100%-4rem)] h-[min(100%-4rem,650px)] max-w-[min(100%-2rem,40rem)]'>
        <DialogTitle className='sr-only'>Cookie Preferences</DialogTitle>
        <DialogDescription className='sr-only'>Cookie Preferences</DialogDescription>
        <div className='px-4 sm:px-8 pt-4 pb-5 border-b border-black/10 h-fit'>
          <Logo className='[&_[data-logo-character]]:fill-black h-8 w-auto' />
        </div>
        <ScrollArea className='flex flex-col px-4 sm:px-8 flex-1'>
          <div className='flex flex-col gap-4 py-4'>
            <div className='header flex flex-col gap-4'>
              {title && <h2 className='h5 font-semibold'>{title}</h2>}
              <p className='text-sm link-primary'>
                {body}
                <br />
                {cookiePolicyLabel && (
                  <NavigationLink href={cookiePolicyLink} target='_blank'>
                    {cookiePolicyLabel}
                  </NavigationLink>
                )}
              </p>
            </div>
            {isSomeUnchecked && allowAllLabel && (
              <Button size='md' className='w-fit' onClick={handleAllowAll}>
                {allowAllLabel}
              </Button>
            )}
            <ManageConsentPreferences
              data={rest}
              checkListMap={checkListMap}
              setCheckListMap={setCheckListMap}
              cookiePreferencesName={cookiePreferencesName}
              clearTimeout={_clearTimeout}
            />
          </div>
        </ScrollArea>
        <div className='px-4 sm:px-8 py-4 border-t border-black/10 flex flex-col sm:flex-row sm:items-center gap-4 justify-end h-fit'>
          {rejectLabel && (
            <Button size='md' className='flex-1 sm:flex-none' onClick={handleReject}>
              {rejectLabel}
            </Button>
          )}
          {confirmLabel && (
            <Button size='md' className='flex-1 sm:flex-none' onClick={handleConfirm}>
              {confirmLabel}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
